<script setup>
import MobileButtonsDialog from 'components/UI/MobileButtonsDialog.vue'
import { useQuasar } from 'quasar'
import { computed, ref } from 'vue'

const props = defineProps({
  actions: {
    type: Array,
    required: false,
    default: () => [],
  },

  hideCancel: {
    type: Boolean,
    required: false,
    default: false,
  },

  cancelAction: {
    type: Function,
    required: true,
  },

  cancelColor: {
    type: String,
    required: false,
    default: 'negative',
  },

  cancelLabel: {
    type: String,
    required: false,
    default: undefined,
  },

  loading: {
    type: Boolean,
    required: false,
    default: false,
  },

  maxButtonsPerBreakpoint: {
    type: Object,
    required: false,
    default: undefined,
  },

  moreLabel: {
    type: String,
    required: false,
    default: 'More actions',
  },

  moreIcon: {
    type: String,
    required: false,
    default: 'more_vert',
  },
})

const $q = useQuasar()
const defaultBreakingoints = {
  xs: 2,
  sm: 3,
  md: 3,
  lg: 4,
  xl: 4,
  xxl: 4,
}
const buttonDialog = ref(false)
const isSmallScreen = computed(() => $q.screen.lt.md)

const isLoading = computed(() => props.actions.some((button) => button.loading === true) || props.loading === true)

const totalSlotsForBreakpoint = computed(() => {
  const screenName = $q.screen.name || 'md'
  const map = props.maxButtonsPerBreakpoint ? { ...defaultBreakingoints, ...props.maxButtonsPerBreakpoint } : defaultBreakingoints

  if (typeof map[screenName] === 'number') {
    return map[screenName]
  }

  const preferredOrder = ['xs', 'sm', 'md', 'lg', 'xl', 'xxl']
  for (const bp of preferredOrder) {
    if (typeof map[bp] === 'number') {
      return map[bp]
    }
  }

  return 2
})

const visibleButtons = computed(() => {
  return (props.actions || []).filter((button) => button && button.show)
})

const splitButtons = computed(() => {
  const buttons = visibleButtons.value
  const totalSlots = totalSlotsForBreakpoint.value

  if (totalSlots <= 0 || buttons.length === 0) {
    return {
      main: [],
      extra: [],
    }
  }

  const slotsForButtons = props.hideCancel ? totalSlots : Math.max(totalSlots - 1, 0)

  const main = buttons.slice(0, slotsForButtons)
  const extra = buttons.slice(slotsForButtons)

  return {
    main,
    extra,
  }
})

const getVisibleGroupButtons = (button) => {
  if (!button || !Array.isArray(button.buttonGroup)) return []
  return button.buttonGroup.filter((child) => child && child.show)
}

/**
 * Small screen: we willen dat ook de buttonGroup-items meetellen in max slots.
 * We flatten daarom main+groups naar leaf-acties en knippen daarop:
 * - mainLeafButtonsSmall: eerste slotsForButtons leaf-acties
 * - extraLeafButtonsSmall: de rest
 */
const leafButtonsSmall = computed(() => {
  const result = []
  for (const button of visibleButtons.value) {
    // parent eerst
    result.push(button)
    // dan zijn zichtbare group-items
    const groupButtons = getVisibleGroupButtons(button)
    if (groupButtons.length) {
      result.push(...groupButtons)
    }
  }
  return result
})

const smallScreenSplit = computed(() => {
  const totalSlots = totalSlotsForBreakpoint.value
  if (totalSlots <= 0 || leafButtonsSmall.value.length === 0) {
    return {
      main: [],
      extra: [],
    }
  }

  const slotsForButtons = props.hideCancel ? totalSlots : Math.max(totalSlots - 1, 0)

  const main = leafButtonsSmall.value.slice(0, slotsForButtons)
  const extra = leafButtonsSmall.value.slice(slotsForButtons)

  return {
    main,
    extra,
  }
})

const displayMainButtons = computed(() => {
  return isSmallScreen.value ? smallScreenSplit.value.main : splitButtons.value.main
})

const flattenedExtraButtons = computed(() => {
  if (isSmallScreen.value) {
    // Small: extra = leaf-split extra (parent + group items al geflat)
    return smallScreenSplit.value.extra
  }

  // Desktop / tablet: flatten zoals eerder
  const result = []

  for (const button of splitButtons.value.extra) {
    const groupButtons = getVisibleGroupButtons(button)

    result.push(button)

    if (groupButtons.length) {
      result.push(...groupButtons)
    }
  }

  return result
})

const hasExtraButtons = computed(() => flattenedExtraButtons.value.length > 0)

const handleButtonClick = async (button) => {
  if (props.loading || button.disabled) return
  if (typeof button.action === 'function') {
    await button.action()
  }
}
</script>

<template>
  <div
    class="items-end q-pr-xs"
    :class="{
      col: $q.screen.lt.md,
      'col-auto': !$q.screen.lt.md,
      'q-pl-xs': buttonIndex !== 0,
      'q-pr-sm': hideCancel && buttonIndex === displayMainButtons.length - 1,
    }"
    v-for="(button, buttonIndex) in displayMainButtons"
    :key="button.name || buttonIndex"
  >
    <q-btn-dropdown
      v-if="getVisibleGroupButtons(button).length && !$q.screen.lt.md && visibleButtons.includes(button)"
      :aria-label="button.label"
      :class="[$q.screen.lt.md ? 'full-width' : 'btn-min-width-100', button.class]"
      :disable="button.customProps?.loading || button.customProps?.disable"
      @click="handleButtonClick(button)"
      split
      no-caps
      color="primary"
      v-bind="button.customProps"
    >
      <q-list>
        <q-item
          v-for="(groupButton, groupIndex) in getVisibleGroupButtons(button)"
          :key="groupButton.name || groupIndex"
          clickable
          class="non-selectable"
          v-close-popup
          :disable="groupButton.customProps?.disable"
          @click="handleButtonClick(groupButton)"
        >
          <q-item-section v-if="groupButton.customProps?.icon" avatar>
            <q-icon :name="groupButton.customProps?.icon" />
          </q-item-section>

          <q-item-section>
            <q-item-label>{{ groupButton.customProps?.label }}</q-item-label>
            <q-item-label v-if="groupButton.tooltip" caption>
              {{ groupButton.tooltip }}
            </q-item-label>
          </q-item-section>
        </q-item>
      </q-list>
    </q-btn-dropdown>

    <q-btn
      v-else
      :aria-label="button.label"
      @click="handleButtonClick(button)"
      :class="{ 'full-width': $q.screen.lt.md, 'btn-min-width-100': !$q.screen.lt.md }"
      :disable="button.customProps?.loading || button.customProps?.disable"
      color="primary"
      no-caps
      v-bind="button.customProps"
    />
  </div>

  <div v-if="!hideCancel" class="items-end q-pl-xs" :class="{ 'col-4': $q.screen.lt.md, 'col-auto': !$q.screen.lt.md, 'q-pr-sm q-pr-md-md': !hasExtraButtons }">
    <q-btn
      :label="cancelLabel || $t('Globals.Cancel')"
      :color="cancelColor"
      class="cancel-button"
      :class="[$q.screen.lt.md ? 'full-width' : 'btn-min-width-100']"
      :disable="isLoading"
      no-caps
      @click="cancelAction()"
    />
  </div>

  <div class="column col-auto q-pl-xs q-pr-xs justify-center" v-if="hasExtraButtons">
    <q-icon :name="moreIcon" flat @click="isSmallScreen ? (buttonDialog = true) : null" class="cursor-pointer font-size-rem-16">
      <q-menu anchor="top end" v-if="!isSmallScreen" self="bottom end" :offset="[-5, 20]" transition-show="jump-down" transition-hide="jump-up" class="q-pa-xs">
        <q-card flat class="q-pa-none min-width-px-200 max-width-px-200">
          <q-card-section class="q-pa-none full-width bg-transparent">
            <div class="row buttons-container bg-white">
              <div class="col-12 q-pa-xs q-my-auto">
                <q-btn
                  v-for="(button, index) in flattenedExtraButtons"
                  class="full-width"
                  @click="handleButtonClick(button)"
                  :aria-label="button.label"
                  :disable="button.customProps?.loading || button.customProps?.disable"
                  :key="button.name || index"
                  color="primary"
                  no-caps
                  v-close-popup
                  :class="{ 'q-mb-sm': index !== flattenedExtraButtons.length - 1 }"
                  v-bind="button.customProps"
                >
                  <q-tooltip v-if="(!$q.screen.gt.md || !button.label) && (button.tooltip || button.label)" :delay="500">
                    {{ button.tooltip || button.label }}
                  </q-tooltip>
                </q-btn>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </q-menu>
    </q-icon>
  </div>

  <mobile-buttons-dialog v-model:show="buttonDialog">
    <div
      class="button-padding"
      :class="{ col: $q.screen.lt.md, 'col-auto': !$q.screen.lt.md }"
      v-for="(button, buttonIndex) in flattenedExtraButtons"
      :key="button.name || buttonIndex"
    >
      <q-btn
        :aria-label="button.label"
        v-close-popup
        @click="handleButtonClick(button)"
        :class="[$q.screen.lt.md ? 'full-width' : 'btn-min-width-100', button.class]"
        :disable="button.customProps?.loading || button.customProps?.disable"
        no-caps
        v-bind="button.customProps"
      />
    </div>
  </mobile-buttons-dialog>
</template>
